"use strict";
/**
 * THE DISCHARGE JOURNEY — one definition, read by the server (which builds the worklist and the stay's file from it) and by
 * the Discharge screens (which colour, order and filter by it), so the two cannot disagree about what "Ready to discharge" means.
 *
 * WHY STAGES AT ALL, WHEN THE STAY ALREADY HAS A STATUS
 *
 * `IpAdmissionStatus` is the lifecycle the STATE MACHINE needs (ADMITTED, DISCHARGE_ADVISED, PENDING_CLEARANCE, BILL_FINALISED,
 * DISCHARGED, EXITED, ...). A discharge desk asks a different question: "who is waiting on whom?". Two stays that are both
 * DISCHARGE_ADVISED are in very different places — one is waiting for the patient's family to pay, the other has paid and is
 * only waiting for the bill to settle itself — and the desk chases them differently. So the status is read together with the
 * live balance, and the answer is one of five stages the whole building can say aloud:
 *
 *   ADVISED     discharge advised, balance still to be paid            -> chase the family / the cash counter
 *   BILLING     advised, nothing owed, bill not yet settled/posted     -> chase billing (it should settle itself)
 *   READY       bill settled and posted                                -> complete the discharge
 *   DISCHARGED  discharge completed, patient not yet recorded as out   -> gate pass / belongings / handover
 *   EXITED      patient has left                                       -> done
 *
 * A stay that is ADMITTED, CANCELLED or awaiting a cancellation decision is NOT in the journey (`null`).
 *
 * NOTHING HERE TOUCHES MONEY. The balance is the server's own live figure (`IpAdvancePositionQueries`), passed in; this file
 * compares it with zero and never adds anything up.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DISCHARGE_CHECKLIST_CODES = exports.DISCHARGE_CHECKLIST = exports.DISCHARGE_OUTCOME_LABEL = exports.DISCHARGE_OUTCOMES = exports.DISCHARGE_TAT_TARGET_MINUTES = exports.DISCHARGE_PIPELINE_STATUSES = exports.DISCHARGE_STAGE_INFO = exports.DISCHARGE_STAGES = void 0;
exports.dischargeStageOf = dischargeStageOf;
exports.minutesBetween = minutesBetween;
exports.isDischargeDelayed = isDischargeDelayed;
exports.formatWait = formatWait;
exports.outcomeNeedsRemarks = outcomeNeedsRemarks;
exports.isInsuredPayer = isInsuredPayer;
exports.checklistFor = checklistFor;
exports.DISCHARGE_STAGES = ['ADVISED', 'BILLING', 'READY', 'DISCHARGED', 'EXITED'];
exports.DISCHARGE_STAGE_INFO = {
    ADVISED: { label: 'Awaiting payment', next: 'Balance is due — collect it at the cash counter.', waitingOn: 'Patient / family' },
    BILLING: { label: 'Settling bill', next: 'Nothing owed — the bill should settle itself; settle it by hand if it has not.', waitingOn: 'Billing' },
    READY: { label: 'Ready to discharge', next: 'Bill is settled and posted — complete the discharge.', waitingOn: 'Discharge desk' },
    DISCHARGED: { label: 'Discharged', next: 'Hand over the file and record the exit when the patient leaves.', waitingOn: 'Ward / gate' },
    EXITED: { label: 'Left hospital', next: 'Complete.', waitingOn: '—' },
};
/** The statuses that put a stay on the discharge pipeline at all. */
exports.DISCHARGE_PIPELINE_STATUSES = ['DISCHARGE_ADVISED', 'PENDING_CLEARANCE', 'BILL_FINALISED', 'DISCHARGED'];
function dischargeStageOf(input) {
    switch (input.status) {
        case 'DISCHARGE_ADVISED':
            return typeof input.outstandingPaise === 'number' && input.outstandingPaise <= 0 ? 'BILLING' : 'ADVISED';
        // The legacy clearance branch: nothing offers it any more, but one stay may still sit there. It is billing's to move.
        case 'PENDING_CLEARANCE':
            return 'BILLING';
        case 'BILL_FINALISED':
            return 'READY';
        case 'DISCHARGED':
            return 'DISCHARGED';
        case 'EXITED':
            return 'EXITED';
        default:
            return null;
    }
}
/**
 * HOW LONG A DISCHARGE MAY TAKE, from the click that advised it to the patient being discharged.
 *
 * Three hours is the figure large Indian hospital groups publish for a routine cash or insured discharge (Apollo and Manipal
 * both run "discharge within 2-3 hours" programmes), and it is the default here. It is ONE constant so that moving it is a
 * one-line change rather than a hunt — and it is a target the desk reads, not a rule the server enforces: no stay is ever
 * refused or held because of it.
 */
exports.DISCHARGE_TAT_TARGET_MINUTES = 180;
/** Whole minutes between two instants, never negative; `null` when either is missing or unreadable. */
function minutesBetween(from, to) {
    if (!from || !to)
        return null;
    const a = (from instanceof Date ? from : new Date(from)).getTime();
    const b = (to instanceof Date ? to : new Date(to)).getTime();
    if (Number.isNaN(a) || Number.isNaN(b))
        return null;
    return Math.max(0, Math.round((b - a) / 60_000));
}
/**
 * Is a stay that is still waiting past the target? Measured from the ADVICE (a real click) and only while the stay has not been
 * discharged — a finished discharge is judged by its turnaround, not by the clock.
 */
function isDischargeDelayed(advisedAt, stage, now = new Date(), targetMinutes = exports.DISCHARGE_TAT_TARGET_MINUTES) {
    if (stage === null || stage === 'DISCHARGED' || stage === 'EXITED')
        return false;
    const waited = minutesBetween(advisedAt, now);
    return waited !== null && waited > targetMinutes;
}
/** "2 h 15 min", "45 min", "1 d 3 h" — the way a ward says a wait. */
function formatWait(minutes) {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes))
        return '—';
    const m = Math.max(0, Math.round(minutes));
    if (m < 60)
        return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return m % 60 === 0 ? `${h} h` : `${h} h ${m % 60} min`;
    const d = Math.floor(h / 24);
    return h % 24 === 0 ? `${d} d` : `${d} d ${h % 24} h`;
}
// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------
/** The four ways a stay can end, as `AdmissionDischargeStatus` stores them. */
exports.DISCHARGE_OUTCOMES = ['DISCHARGED', 'DAMA', 'TRANSFERRED', 'DECEASED'];
exports.DISCHARGE_OUTCOME_LABEL = {
    DISCHARGED: 'Discharged — routine',
    DAMA: 'Discharged against medical advice (DAMA)',
    TRANSFERRED: 'Referred / transferred to another facility',
    DECEASED: 'Deceased',
};
/**
 * An outcome other than a routine discharge needs a reason on the record — a DAMA is a legal statement, a referral names where
 * the patient went, a death is a medico-legal event. The server stores `remarks` as optional, so this is the desk's rule, not
 * the API's; it is stated once here so the screen and any report agree.
 */
function outcomeNeedsRemarks(outcome) {
    return outcome !== 'DISCHARGED';
}
exports.DISCHARGE_CHECKLIST = [
    {
        code: 'NURSING_CLEARANCE',
        label: 'Nursing clearance',
        owner: 'Nursing',
        hint: 'IV lines and tubes removed, patient stable at discharge, belongings returned.',
        appliesTo: 'ALL',
    },
    {
        code: 'DISCHARGE_MEDICINES',
        label: 'Discharge medicines issued',
        owner: 'Pharmacy',
        hint: 'Take-home medicines issued and counselled; ward returns settled.',
        appliesTo: 'ALL',
    },
    {
        code: 'PATIENT_EDUCATION',
        label: 'Instructions and follow-up explained',
        owner: 'Nursing',
        hint: 'Diet, medicines, warning signs and the review date explained to the patient or attendant.',
        appliesTo: 'ALL',
    },
    {
        code: 'INSURER_DOCUMENTS',
        label: 'Insurer / TPA documents complete',
        owner: 'Insurance desk',
        hint: 'Final bill, discharge summary, investigation reports and claim forms ready for the insurer.',
        appliesTo: 'INSURED',
    },
    {
        code: 'FILE_HANDED_OVER',
        label: 'Discharge file handed over',
        owner: 'Discharge desk',
        hint: 'Summary, final bill, reports and prescriptions given; the patient or attendant has acknowledged receipt.',
        appliesTo: 'ALL',
    },
];
exports.DISCHARGE_CHECKLIST_CODES = exports.DISCHARGE_CHECKLIST.map((item) => item.code);
/** `INSURANCE`, `TPA` and `GOVERNMENT_SCHEME` stays go to a third party; the rest settle with the patient. */
function isInsuredPayer(payerType) {
    return payerType === 'INSURANCE' || payerType === 'TPA' || payerType === 'GOVERNMENT_SCHEME';
}
function checklistFor(payerType) {
    const insured = isInsuredPayer(payerType);
    return exports.DISCHARGE_CHECKLIST.filter((item) => item.appliesTo === 'ALL' || insured);
}
//# sourceMappingURL=discharge-journey.js.map